import type { Container, Filter } from "pixi.js";
import { ShineSweepFilter, type PresentationTaskRunner } from "@slot/presentation";
import type { GlintTuning } from "../tuning/shineTuning";

interface Glint {
  filter: ShineSweepFilter;
  generation: number;
}

/**
 * The filter's band for `node`: it measures in the element's own frame (0..1 across, 0..1 down), so a streak the same
 * on every element divides by the element's size — its widths by the width, its lean by the aspect.
 */
function bandIn(node: Container, look: GlintTuning): { tilt: number; width: number; softness: number } {
  const { width, height } = node.getLocalBounds();
  const w = Math.max(1, width);
  return { tilt: (look.lean * height) / w, width: look.width / w, softness: look.softness / w };
}

/** A node's filters as a list (Pixi takes one filter or several). */
function filtersOf(node: Container): Filter[] {
  const current = node.filters as Filter | readonly Filter[] | null | undefined;
  if (!current) return [];
  return Array.isArray(current) ? [...current] : [current as Filter];
}

/**
 * A glint across a whole element (the frame, a plaque with its amount, a pot, a coin): the engine's `ShineSweepFilter`
 * (a tilted band brightening the element's own pixels, its colours and its shape kept) on the element for one sweep,
 * then taken off — so an element costs nothing between glints, and the band runs straight across every sprite inside
 * it (the engine's per-sprite sweep would bend it at the frame's slices). Another glint on an element restarts it;
 * the element's own filters stay as they are.
 */
export class ShineSweeper {
  private readonly glints = new Map<Container, Glint>();
  private generation = 0;

  /** One sweep across `node` on `runner`, outside every scope: a glint is never work a round waits for. */
  public async sweep(node: Container, look: GlintTuning, runner: PresentationTaskRunner): Promise<void> {
    if (node.destroyed) return;
    this.stop(node);
    const filter = new ShineSweepFilter({ highlight: look.peak, ...bandIn(node, look), progress: 0 });
    // A filter draws its element into a texture first, by default at one pixel per unit and without antialiasing: on a
    // high-density screen the element would go soft while it glints. At the screen's own, it stays as sharp as it is.
    filter.resolution = "inherit";
    filter.antialias = "inherit";
    node.filters = [...filtersOf(node), filter];
    const generation = ++this.generation;
    this.glints.set(node, { filter, generation });
    await runner.tweenProgress(
      look.seconds,
      (k) => {
        if (this.glints.get(node)?.generation === generation) filter.progress = k;
      },
      undefined,
      { easing: look.easing },
    );
    if (this.glints.get(node)?.generation === generation) this.stop(node);
  }

  /** The glint on `node` (if any) comes off at once. */
  public stop(node: Container): void {
    const glint = this.glints.get(node);
    if (!glint) return;
    this.glints.delete(node);
    if (!node.destroyed) {
      const rest = filtersOf(node).filter((f) => f !== glint.filter);
      node.filters = rest.length > 0 ? rest : null;
    }
    glint.filter.destroy();
  }

  /** Every glint on the nodes `which` picks comes off at once (a new roll: the coins' views go back to the pool). */
  public stopWhere(which: (node: Container) => boolean): void {
    for (const node of Array.from(this.glints.keys())) if (which(node)) this.stop(node);
  }

  /** The elements glinting now. */
  public get glinting(): readonly Container[] {
    return Array.from(this.glints.keys());
  }
}
